import { useCallback, useContext, useMemo, useState, useRef } from "react";
import { AdministrarContext } from "./context";
import SoftBox from "components/SoftBox";
import {
	Card,
	Grid,
	Icon,
	InputLabel,
	MenuItem,
	Select,
	Tooltip,
	Snackbar,
	Alert,
	Collapse,
	TextField,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Button,
} from "@mui/material";
import SoftTypography from "components/SoftTypography";
import { getUproColor } from "utils/colors";
import DinamicCard from "components/Cards/InfoCards/DinamicCard";
import { ConfirmarContext } from "context/ConfirmarContext";
import { formatMoneyPunto } from "utils/formatters";
import SinResultados from "components/Carteles/SinResultados";
import Loading from "components/Carteles/Loading";
import SoftInput from "components/SoftInput";
import { gql, useLazyQuery, useQuery } from "@apollo/client";
import SinDatos from "components/Carteles/SinDatos";
import SoftInputNumber from "components/SoftInputNumberUpgrade";
import SoftButton from "components/SoftButton";
import { UserContext } from "context/user";
import DataTableCustom from "components/Tables/DataTableCustom";
import { getColumns } from "./tableConfig";
import { cambiarUnidadCompra } from "./unidadCompra";
import {
	calcularTotalEstimado,
	detallesVigentes,
	mpPorPack,
	puedeEditarDetalles,
	yaAgregadaSinPack,
} from "./reglasPedido";

const idTemporal = () => `temp_${Date.now()}_${Math.floor(Math.random() * 100000)}`;
import { MenuContext } from "context/menuContext";

export default function Tab1(args) {

	// CONTEXTS
	const { user: currentUser } = useContext(UserContext);
	const { data, setData, dataToSave, mode, fullscreen, getError, marcarTocado } =
		useContext(AdministrarContext);
	const { confirmar } = useContext(ConfirmarContext);
	const { menu } = useContext(MenuContext);

	// STATES
	const [searchTimeout, setSearchTimeout] = useState(null);
	const [servicio, setServicio] = useState(null);
	const [openSelectionMenu, setOpenSelectionMenu] = useState(false);
	const [openSnackbar, setOpenSnackbar] = useState(false);
	const [openModalProductosFinales, setOpenModalProductosFinales] = useState(false);

	// Estados de Modal Productos Finales
	const [pfSearch, setPfSearch] = useState("");
	const [pfSelected, setPfSelected] = useState(null);
	const [pfCantidad, setPfCantidad] = useState(1);
	const [pfOpenResults, setPfOpenResults] = useState(false);
	// Expand/Collapse por PF en el panel global
	const [pfGlobalExpanded, setPfGlobalExpanded] = useState({});

	// Memo: resumen de Productos Finales a partir de data.detalles
	const resumenPF = useMemo(() => {
		const resumenMap = new Map();
		const detalles = data?.detalles || [];
		detalles.forEach((det) => {
			const fuentes = det?.fuentesPF || [];
			fuentes.forEach((f) => {
				const key = String(f.idProducto);
				const item = resumenMap.get(key) || {
					idProducto: f.idProducto,
					nombre: f.nombreProducto || f.idProducto,
					porciones: f.cantidadPorciones ?? det.porciones,
					totalPF: 0,
					totalMP: 0,
					usos: 0,
				};
				item.totalPF += Number(f.cantidadPF) || 0;
				item.totalMP += Number(f.cantidadMP) || 0;
				item.cantidadPF = Number(f.cantidadPF) || 0;
				item.usos += 1;
				resumenMap.set(key, item);
			});
		});
		return Array.from(resumenMap.values());
	}, [data?.detalles]);

	const pfIdsAgregados = useMemo(
		() => new Set(resumenPF.map((pf) => String(pf.idProducto))),
		[resumenPF]
	);

	// Memo: desglose por MP por cada PF
	// Mapa: pfId => Array de items de MP { idMateriaPrima, nombre, unidad, aporteMP, usos, filas }
	const desglosesPorPF = useMemo(() => {
		const byPF = new Map();
		const detalles = data?.detalles || [];
		detalles.forEach((det) => {
			const mpId = det?.materiaPrima?.id || det?.idMateriaPrima;
			if (!mpId) return;
			const fuentes = det?.fuentesPF || [];
			if (fuentes.length === 0) return;
			// Agrupar por PF dentro de esta fila para contar 'usos' por fila (no por fuente)
			const aportePorPF = new Map();
			fuentes.forEach((f) => {
				const pfId = f.idProducto;
				const prev = aportePorPF.get(pfId) || 0;
				aportePorPF.set(pfId, prev + (Number(f.cantidadMP) || 0));
			});
			aportePorPF.forEach((aporte, pfId) => {
				if (!byPF.has(pfId)) byPF.set(pfId, new Map());
				const bucket = byPF.get(pfId);
				const item = bucket.get(mpId) || {
					idMateriaPrima: mpId,
					nombre: det?.materiaPrima?.nombre || String(mpId),
					unidad: det?.materiaPrima?.tipoUnidad?.abreviatura || "",
					aporteMP: 0,
					usos: 0,
					filas: [],
				};
				item.aporteMP += aporte;
				item.usos += 1;
				item.filas.push({ id: det.id, numero: det.numero });
				bucket.set(mpId, item);
			});
		});
		const result = new Map();
		byPF.forEach((mpMap, pfId) => {
			result.set(pfId, Array.from(mpMap.values()));
		});
		return result;
	}, [data?.detalles]);

	const [keyFile, setKeyFile] = useState(0);
	// Control de filas expandibles (para editor de observación y otras expansiones)
	const [expandedKeys, setExpandedKeys] = useState([]);
	// Archivo adjunto de un ítem: input oculto compartido y el ítem al que se le carga
	const fileRef = useRef(null);
	const idDetalleArchivo = useRef(null);

	const actualizarDetallePorId = useCallback((id, cambios) => {
		setData((prev) => ({
			...prev,
			detalles: (prev?.detalles || []).map((d) => (d.id === id ? { ...d, ...cambios } : d)),
		}));
	}, [setData]);

	const toggleInput = useCallback((id) => {
		idDetalleArchivo.current = id;
		fileRef.current?.click();
	}, []);

	const handleFile = (event) => {
		const file = event.target.files?.[0];
		if (file && idDetalleArchivo.current != null) {
			actualizarDetallePorId(idDetalleArchivo.current, { fileUpload: file, file: null });
		}
		idDetalleArchivo.current = null;
		// Permite volver a elegir el mismo archivo
		setKeyFile((prev) => prev + 1);
	};

	const handleRemoveFile = useCallback((id) => {
		actualizarDetallePorId(id, { file: null, fileUpload: null });
	}, [actualizarDetallePorId]);

	// GRAPHQL
	const [getProductos, { data: productos, loading }] = useLazyQuery(
		gql`
      query materiasPrimas($page: Int, $order: Order, $filter: JSON) {
        paginationInfo {
          pages
          total
        }
        materiasPrimas(pagination: { page: $page, limit: 50 }, order: $order, filter: $filter) {
          id
          tipo
          nombre
          nombreCompra
          categoria
          precio
          tipoUnidad {
            nombre
            multiplicador
            abreviatura
          }
        }
      }
    `,
		{
			fetchPolicy: "network-only",
		}
	);

	const { data: dataCategorias } = useQuery(
		gql`
      query CategoriasMateriaPrima($tipoProducto: String) {
        categoriasMateriaPrima(filter: { tipoProducto: { _or: [$tipoProducto, null] } }) {
          tipoProducto
          nombre
        }
      }
    `,
		{
			variables: { tipoProducto: "Servicio" },
		}
	);

	const [getProductosFinales, { loading: pfLoading, data: pfData }] = useLazyQuery(
		gql`
      query getProductos(
        $order: Order
        $search: String
        $filter: JSON
        $sinIngredientes: Boolean
        $materiaPrimaDeshabilitada: Boolean
      ) {
        paginationInfo {
          pages
          total
        }
        productos(
          order: $order
          search: $search
          filter: $filter
          sinIngredientes: $sinIngredientes
          materiaPrimaDeshabilitada: $materiaPrimaDeshabilitada
        ) {
          id
          nombre
          disponibilidad
          precioCarta
          precioOnline
          precioPorMayor
          precioPorMayorOnline
          esElaborado
          esIngrediente
          cantidadPorciones
          cantidadPorMayor
          descripcion
          combo
          seccion
          valoracionPromedio
          tipoProducto {
            id
            tipo
          }
          categoriaProducto {
            id
            categoria
          }
          area {
            id
            nombre
          }
          ingredientes {
            id
            cantidad
            materiaPrima {
              id
              nombre
              precio
              porcentajeUtilizable
              disabled
              tipoUnidad {
                id
                nombre
                abreviatura
                multiplicador
              }
            }
            tipoUnidad {
              id
              nombre
              abreviatura
              multiplicador
            }
          }
          descartables {
            id
            cantidad
            materiaPrima {
              id
              nombre
              precio
              disabled
              tipoUnidad {
                id
                nombre
                abreviatura
                multiplicador
              }
            }
            tipoUnidad {
              id
              nombre
              abreviatura
              multiplicador
            }
          }
          files {
            id
            file
          }
          productosCombo {
            id
            nombre
          }
        }
      }
    `,
		{
			fetchPolicy: "network-only",
			notifyOnNetworkStatusChange: true,
		}
	);

	// Query: Productos finales -> Materias Primas consolidadas
	const [getPFMateriasPrimas, { loading: pfmpLoading }] = useLazyQuery(
		gql`
      query productosFinalesMateriasPrimas($idProducto: ID!) {
        productosFinalesMateriasPrimas(idProducto: $idProducto) {
          id
          tipo
          nombre
          nombreCompra
          categoria
          precio
          tipoUnidad { nombre abreviatura multiplicador }
          cantidadIngredienteUnidadMP
        }
      }
    `,
		{ fetchPolicy: "network-only" }
	);

	// HANDLERS
	const handleSearch = (event) => {
		const newTimeout = setTimeout(() => {
			getProductos({
				variables: {
					page: 1,
					filter: {
						disabled: false,
						tipo: "Consumible",
						_or: [
							{
								id: { _like: `%${event.target.value}%` },
							},
							{
								nombre: { _like: `%${event.target.value}%` },
							},
							{
								categoria: { _like: `%${event.target.value}%` },
							},
						],
					},
				},
			});
		}, 500);
		if (searchTimeout) {
			clearTimeout(searchTimeout);
		}
		setSearchTimeout(newTimeout);
	};

	const handleAddDetalle = (producto) => {
		if (yaAgregadaSinPack(data?.detalles, producto?.id)) {
			setOpenSnackbar(true);
			return;
		}
		setData((prev) => {
			if (yaAgregadaSinPack(prev?.detalles, producto?.id)) return prev;
			const nuevoDetalle = {
				id: idTemporal(),
				materiaPrima: producto,
				nombreCompra: producto?.nombre || "",
				cantidad: 1,
				ajusteManual: 1,
				unidad: producto?.tipoUnidad?.abreviatura || null,
				// Precio sugerido: el del catálogo; el usuario puede modificarlo
				precioEstimado: producto?.precio ?? 0,
				observacion: null,
				requierePack: false,
				unidadPack: null,
				cantidadPack: 1,
				// Cantidad expresada en la unidad de la materia prima
				totalMP: 1,
			};
			return {
				...prev,
				detalles: [...(prev?.detalles || []), nuevoDetalle],
			};
		});
	};

	const handleCloseSnackbar = () => {
		setOpenSnackbar(false);
	};

	const handleAddServicio = () => {
		setData((prev) => {
			return {
				...prev,
				detalles: [
					...(prev?.detalles || []),
					{
						// Id temporal para identificar la fila (observación, archivo, quitar) hasta guardar
						id: idTemporal(),
						idMateriaPrima: null,
						cantidad: 1,
						precioEstimado: servicio?.precioEstimado,
						nombreServicio: servicio?.nombreServicio,
						rubroServicio: servicio?.rubro,
						cantidadVerificada: 1,
					},
				],
			};
		});
		setServicio((prev) => ({
			...prev,
			precioEstimado: null,
			nombreServicio: null,
		}));
	};

	// Marca un detalle para eliminar por id (se ocultará de la vista pero se mantiene en dataToSave con _toRemove: true)
	const handleMarkRemove = (id) => {
		setData((prev) => {
			if (!prev?.detalles) return prev;
			const detalles = prev.detalles.map((item) => (item.id === id ? { ...item, _toRemove: true } : item));
			return { ...prev, detalles };
		});
	};

	// Nuevo dialog de observación (solo lectura)
	const [obsViewOpen, setObsViewOpen] = useState(false);
	const [obsViewText, setObsViewText] = useState("");
	const openObservacionDialog = useCallback((id) => {
		const found = data?.detalles?.find(d => d.id === id);
		setObsViewText(found?.observacion || "");
		setObsViewOpen(true);
	}, [data?.detalles]);
	const closeObservacionDialog = () => {
		setObsViewOpen(false);
		setObsViewText("");
	};

	const handleChangeTipoDetalle = (e) => {
		const value = e.target.value;
		if (value === data?.tipoDetalle) return;
		const aplicar = () => {
			marcarTocado("tipoDetalle");
			setData((prev) => ({
				...prev,
				detalles: [],
				tipoDetalle: value,
			}));
		};

		const cantidadItems = detallesVigentes(data?.detalles).length;
		if (cantidadItems === 0) {
			aplicar();
			return;
		}
		confirmar({
			icon: "warning",
			title: "Cambiar tipo de detalle",
			message: `Al cambiar a ${value} se quitarán los ${cantidadItems} ítem${cantidadItems === 1 ? "" : "s"} cargado${cantidadItems === 1 ? "" : "s"}. ¿Desea continuar?`,
			confirmLabel: "Cambiar y quitar ítems",
			cancelLabel: "Cancelar",
			func: aplicar,
		});
	};

	const handleChangeServicio = (e) => {
		setServicio({ ...servicio, [e.target.name]: e.target.value });
	};

	const handleUpdateDetalle = useCallback((detalle, indexOrId) => {
		// Usar setData con función para evitar closures obsoletos y trabajar con el snapshot más reciente
		setData((prev) => {
			const detalles = prev?.detalles ?? [];
			if (detalles.length === 0) return prev;

			// Determinar índice: si viene un número válido, usarlo; si no, buscar por id
			let index = null;
			if (typeof indexOrId === 'number') {
				index = indexOrId >= 0 && indexOrId < detalles.length ? indexOrId : null;
			}
			if (index === null) {
				const idToFind = detalle?.id ?? indexOrId;
				index = detalles.findIndex((d) => d.id === idToFind);
			}
			if (index === null || index === -1) return prev;

			const nuevos = detalles.map((item, i) => (i === index ? detalle : item));
			return { ...prev, detalles: nuevos };
		});
	}, [setData]);

	// ===== Editor de observación (expandible) =====
	const startEditObservacion = useCallback((id) => {
		setData((prev) => {
			const detalles = prev?.detalles || [];
			const index = detalles.findIndex((d) => d.id === id);
			if (index === -1) return prev;
			const item = detalles[index];
			const nuevo = { ...item, _editObservacion: true, _observacionDraft: item?.observacion || "" };
			const nuevos = detalles.map((d, i) => (i === index ? nuevo : d));
			return { ...prev, detalles: nuevos };
		});
		setExpandedKeys((prev) => (prev.includes(id) ? prev : [...prev, id]));
	}, [setData]);

	const saveEditObservacion = useCallback((id) => {
		const detalles = data?.detalles || [];
		const index = detalles.findIndex((d) => d.id === id);
		if (index === -1) return;
		const item = detalles[index];
		const nuevo = { ...item };
		nuevo.observacion = item?._observacionDraft || "";
		delete nuevo._observacionDraft;
		delete nuevo._editObservacion;
		handleUpdateDetalle(nuevo, id);
		setExpandedKeys((prev) => prev.filter((k) => k !== id));
	}, [data?.detalles, handleUpdateDetalle]);

	const cancelEditObservacion = useCallback((id) => {
		const detalles = data?.detalles || [];
		const index = detalles.findIndex((d) => d.id === id);
		if (index === -1) return;
		const item = detalles[index];
		const nuevo = { ...item };
		delete nuevo._observacionDraft;
		delete nuevo._editObservacion;
		handleUpdateDetalle(nuevo, id);
		setExpandedKeys((prev) => prev.filter((k) => k !== id));
	}, [data?.detalles, handleUpdateDetalle]);

	// ==== Helpers de fuentesPF ====
	const sumFuentesMP = useCallback((item) => {
		return (item?.fuentesPF || []).reduce((acc, f) => acc + (Number(f?.cantidadMP) || 0), 0);
	}, []);

	const recalcFromFuentes = useCallback((item) => {
		const sumFuentes = sumFuentesMP(item);
		const ajusteManual = Number(item?.ajusteManual) || 0;
		const total = sumFuentes + ajusteManual;
		const recalculado = {
			...item,
			cantidad: total,
			totalMP: total,
			// Se conserva el precio que haya cargado el usuario; si no hay, se sugiere el del catálogo
			precioEstimado: item?.precioEstimado ?? item?.materiaPrima?.precio ?? 0,
		};
		delete recalculado._cantidadSinRedondear;
		delete recalculado._cantidadRedondeadaManual;
		return recalculado;
	}, [sumFuentesMP]);

	const restoreOriginalAjusteAfterFuenteChange = useCallback((item, previousSumFuentes) => {
		if (!item?._cantidadRedondeadaManual || item?._cantidadSinRedondear == null) {
			return item;
		}

		const originalCantidad = Number(item._cantidadSinRedondear) || 0;
		const originalAjusteManual = originalCantidad - (Number(previousSumFuentes) || 0);
		const normalizado = {
			...item,
			ajusteManual: originalAjusteManual,
		};
		delete normalizado._cantidadSinRedondear;
		delete normalizado._cantidadRedondeadaManual;
		return normalizado;
	}, []);

	const toggleCantidadRedondeada = useCallback((id) => {
		setData((prev) => {
			const detalles = prev?.detalles || [];
			const index = detalles.findIndex((d) => d.id === id);
			if (index === -1) return prev;

			const item = { ...detalles[index] };
			if (item?.requierePack) return prev;

			const sumFuentes = Array.isArray(item?.fuentesPF)
				? item.fuentesPF.reduce((acc, f) => acc + (Number(f?.cantidadMP) || 0), 0)
				: 0;

			if (item?._cantidadRedondeadaManual && item?._cantidadSinRedondear != null) {
				const cantidadOriginal = Math.max(0, Number(item._cantidadSinRedondear) || 0);
				item.cantidad = cantidadOriginal;
				item.totalMP = cantidadOriginal;
				item.ajusteManual = cantidadOriginal - sumFuentes;
				delete item._cantidadSinRedondear;
				delete item._cantidadRedondeadaManual;
			} else {
				const cantidadActual = Math.max(0, Number(item?.cantidad) || 0);
				if (cantidadActual === 0) return prev;
				const cantidadRedondeada = cantidadActual <= 0.5
					? 0.5
					: Math.ceil(cantidadActual * 2) / 2;
				item._cantidadSinRedondear = cantidadActual;
				item._cantidadRedondeadaManual = true;
				item.cantidad = cantidadRedondeada;
				item.totalMP = cantidadRedondeada;
				item.ajusteManual = cantidadRedondeada - sumFuentes;
			}

			const nuevos = detalles.map((d, i) => (i === index ? item : d));
			return { ...prev, detalles: nuevos };
		});
	}, [setData]);

	// ===== Equivalencia Pack <-> MP =====
	const [equivOpen, setEquivOpen] = useState(false);
	const [equivIndex, setEquivIndex] = useState(null);
	const [equivPacks, setEquivPacks] = useState(1);
	const [equivMP, setEquivMP] = useState(1);
	const [equivUnidadPack, setEquivUnidadPack] = useState("");

	const openEquivalenciaDialog = useCallback((id) => {
		const index = data?.detalles?.findIndex((d) => d.id === id);
		if (index == null || index === -1) return;
		const item = data?.detalles?.[index];
		if (!item?.requierePack) return; // ícono ya debería estar deshabilitado, pero evitamos abrir por seguridad
		const packs = Math.max(1, Math.round(item?.equivalenciaPack?.packs ?? 1));
		const mp = Number(item?.equivalenciaPack?.materiaPrima ?? 1) || 1;
		setEquivIndex(index);
		setEquivPacks(packs);
		setEquivMP(mp);
		setEquivUnidadPack(item?.unidadPack || "");
		setEquivOpen(true);
	}, [data?.detalles]);

	const closeEquivalenciaDialog = () => {
		setEquivOpen(false);
		setEquivIndex(null);
	};

	const saveEquivalencia = () => {
		if (equivIndex == null) return closeEquivalenciaDialog();
		const detalle = data?.detalles?.[equivIndex];
		if (!detalle) return closeEquivalenciaDialog();

		// Validar unidad de pack
		const unidad = (equivUnidadPack || "").trim();
		if (!unidad || unidad.length === 0 || unidad.length > 30) {
			return; // No guardar si no cumple; el botón debería estar deshabilitado, esto es defensa extra
		}

		// Normalizar valores (evitar división por cero)
		const equivalenciaPack = {
			packs: Math.max(1, Math.round(Number(equivPacks) || 1)),
			materiaPrima: Math.max(0.000001, Number(equivMP) || 1),
		};
		const cantidad = Number(detalle?.cantidad) || 0;

		const nuevoDetalle = cambiarUnidadCompra(detalle, {
			unidadPack: unidad,
			equivalenciaPack,
		});
		handleUpdateDetalle(nuevoDetalle, equivIndex);
		setEquivOpen(false);
		setEquivIndex(null);
	};

	const allowEdit = puedeEditarDetalles(data, mode, currentUser);

	// Resultados del buscador sin las materias primas que ya están en el pedido sin pack
	const materiasPrimasDisponibles = useMemo(
		() => (productos?.materiasPrimas || []).filter((mp) => !yaAgregadaSinPack(data?.detalles, mp.id)),
		[productos, data?.detalles]
	);

	// Mismo total que se usa al guardar para validar el tipo de compra según los montos.
	const total = useMemo(
		() => calcularTotalEstimado(data?.detalles, data?.tipoDetalle),
		[data?.detalles, data?.tipoDetalle]
	);

	// Configuración de DataTableCustom
	const columns = useMemo(
		() => getColumns(
			data?.tipoDetalle,
			allowEdit,
			menu,
			{
				handleMarkRemove,
				handleUpdateDetalle,
				openEquivalenciaDialog,
				toggleInput,
				handleRemoveFile,
				toggleCantidadRedondeada,
				startEditObservacion,
				openObservacionDialog,
			}
		),
		[data?.tipoDetalle, allowEdit, menu, handleMarkRemove, handleUpdateDetalle, openEquivalenciaDialog, toggleCantidadRedondeada, startEditObservacion, openObservacionDialog]
	);

	// La validación de los campos del pliego vive en reglasPedido.js; acá solo se
	// registra la edición y se avisa de las consecuencias de la entrega inmediata.
	const handlePliegoField = useCallback(
		(name, value) => {
			marcarTocado(name);
			if (name === "pliegoRequiereDemanda" && value === "NO" && !data?.pliegoRequiereDemanda) {
				confirmar({
					title: "Entrega inmediata",
					message: "Al seleccionar 'NO', se entenderá que el proveedor deberá entregar el pedido dentro de un plazo de 10 días a partir de la generación de la orden de provisión. ¿Desea continuar?",
					pedirMotivo: false,
				});
			}
		},
		[marcarTocado, data?.pliegoRequiereDemanda, confirmar]
	);

	return (
		<SoftBox>
			{/* Snackbar con el mensaje de advertencia */}
			<Snackbar
				open={openSnackbar}
				autoHideDuration={3000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{
					vertical: "top",
					horizontal: "center",
				}}
				sx={{
					"& .MuiSnackbarContent-root": {
						fontSize: "1.2rem",
						padding: "16px 32px",
						maxWidth: "600px",
						marginLeft: "auto",
						marginRight: "auto",
					},
				}}
			>
				<Alert
					onClose={handleCloseSnackbar}
					severity="error"
					sx={{
						width: "100%",
						fontSize: "1rem",
						padding: "16px 32px",
						border: "0.5  px solid red",
						borderRadius: "8px",
					}}
				>
					Este producto ya fue agregado.
				</Alert>
			</Snackbar>
			{data?.licitacionInicio && (
				<Alert severity="info" sx={{ mb: 2 }}>
					Los detalles y las condiciones del pliego están bloqueados porque ya se inició la cotización.
					Volver a un paso anterior no cancela la cotización ni habilita estos cambios.
				</Alert>
			)}
			<Card sx={{ marginBottom: 2 }}>
				{allowEdit && (
					<>
						{/* SELECT TIPO DE DETALLE */}
						<SoftBox sx={{ width: "100%" }} px={3}>
							<InputLabel htmlFor="tipoDetalle">
								<SoftTypography component="label" variant="caption" fontWeight="bold">
									Tipo de detalle solicitado*
								</SoftTypography>
							</InputLabel>
							<Select
								id="tipoDetalle"
								name="tipoDetalle"
								value={dataToSave?.tipoDetalle || data?.tipoDetalle || ''}
								onChange={handleChangeTipoDetalle}
								error={Boolean(getError("tipoDetalle"))}
								fullWidth
								disabled={data?.tipoDetalle && !dataToSave.tipoDetalle}
							>
								<MenuItem value="Productos">Productos</MenuItem>
								<MenuItem value="Servicios">Servicios</MenuItem>
							</Select>
						</SoftBox>
						{/* FORMULARIO SEGUN TIPO */}
						{data?.tipoDetalle === "Productos" && (
							<SoftBox display="flex" justifyContent="space-between" gap={1} alignItems="center" pr={3}>
								<SoftBox
									sx={{
										width: "100%",
									}}
									p={3}
								>
									<SoftInput
										placeholder="Buscar materias primas consumibles..."
										icon={{ component: "search", direction: "left" }}
										onChange={handleSearch}
										onKeyPress={() => {
											setOpenSelectionMenu(true);
										}}
										onClick={() => {
											getProductos({
												variables: {
													page: 1,
													filter: {
														disabled: false,
														tipo: "Consumible",
													},
												},
											});
											setOpenSelectionMenu(true);
										}}
										onBlur={(e) => {
											let parent = e.relatedTarget?.closest("#custom-autocomplete");
											e.preventDefault();
											if (!parent) {
												setOpenSelectionMenu(false);
											} else {
												e.target.focus();
											}
										}}
									/>
									<SoftBox
										id="custom-autocomplete"
										sx={{ position: "fixed", zIndex: 99, width: "100%" }}
									>
										<a
											href=""
											onClick={(e) => {
												e.preventDefault();
											}}
										>
											<Card>
												<SoftBox
													sx={{
														maxHeight: "240px",
														overflowY: "auto",
														overflowX: "hidden",
													}}
												>
													{openSelectionMenu && loading ? (
														<Loading />
													) : materiasPrimasDisponibles.length > 0 &&
														openSelectionMenu ? (
														materiasPrimasDisponibles
															.map((producto) => (
																<MenuItem
																	key={producto.id}
																	onClick={() => {
																		handleAddDetalle(producto);
																		setOpenSelectionMenu(false);
																	}}
																	sx={{ whiteSpace: "normal" }}
																>
																	<SoftBox mr={2}>
																		<Icon
																			sx={{
																				color:
																					producto.tipo === "Material"
																						? getUproColor("uproYellow")
																						: producto.tipo === "Consumible"
																							? getUproColor("uproGreen")
																							: getUproColor("uproPink"),
																			}}
																			fontSize="small"
																		>
																			{producto.tipo === "Material"
																				? "category"
																				: producto.tipo === "Consumible"
																					? "recycling"
																					: "layers"}
																		</Icon>
																	</SoftBox>
																	<SoftBox sx={{ width: "240px", minWidth: "120px", flex: 1 }}>
																		&nbsp;{`${producto.id} - ${producto.nombre}`}
																	</SoftBox>
																</MenuItem>
															))
													) : !openSelectionMenu ? null : (
														<SinResultados />
													)}
												</SoftBox>
											</Card>
										</a>
									</SoftBox>
								</SoftBox>
								<Tooltip placement="top" title={"Productos finales"}>
									<SoftButton
										variant="gradient"
										color="uproYellow"
										iconOnly
										circular
										onClick={() => setOpenModalProductosFinales(true)}
									>
										<Icon sx={{ fontWeight: "regular" }}>
											shopping_bag
										</Icon>
									</SoftButton>
								</Tooltip>
							</SoftBox>
						)}
						{data?.tipoDetalle === "Servicios" && (
							<Grid container spacing={1} px={3} pt={1}>
								<Grid item xs={12} md={6}>
									<InputLabel htmlFor="rubro">
										<SoftTypography component="label" variant="caption" fontWeight="bold">
											Rubro*
										</SoftTypography>
									</InputLabel>
									<Select
										value={servicio?.rubro}
										onChange={handleChangeServicio}
										fullWidth
										name="rubro"
										sx={{
											height: "140px",
											overflowY: "auto",
											overflowX: "hidden",
										}}
									>
										{dataCategorias?.categoriasMateriaPrima?.filter((categoria) => categoria.tipoProducto === "Servicio").map((categoria, index) => (
											<MenuItem key={index} value={categoria.nombre}>
												{categoria.nombre}
											</MenuItem>
										))}
									</Select>
								</Grid>
								<Grid item xs={12} sm={6}>
									<InputLabel htmlFor="precio">
										<SoftTypography component="label" variant="caption" fontWeight="bold">
											Precio estimado por unidad
										</SoftTypography>
									</InputLabel>
									<SoftInputNumber
										label="Precio"
										placeholder="Precio estimado"
										type="text"
										name="precioEstimado"
										value={servicio?.precioEstimado}
										onChange={handleChangeServicio}
									/>
								</Grid>
								<Grid item xs={12}>
									<InputLabel htmlFor="objetoCompra">
										<SoftTypography component="label" variant="caption" fontWeight="bold">
											Descripción del servicio*
										</SoftTypography>
									</InputLabel>
									<SoftInput
										placeholder="Descripción del servicio"
										type="text"
										name="nombreServicio"
										rows={2}
										multiline
										value={servicio?.nombreServicio || ""}
										onChange={handleChangeServicio}
									/>
									<SoftBox py={1} display="flex" justifyContent="flex-end">
										<SoftButton
											color="primary"
											onClick={handleAddServicio}
											disabled={!servicio?.rubro || !servicio?.nombreServicio}
										>
											<Icon>add</Icon>
										</SoftButton>
									</SoftBox>
								</Grid>
							</Grid>
						)}
					</>
				)}

				<SoftBox
					sx={{
						px: 3
					}}
				>
					{/* Panel global de Productos Finales seleccionados */}
					{Array.isArray(data?.detalles) && data.detalles.length > 0 && (
						<SoftBox mb={2}>
							<SoftTypography variant="h6" color="dark" fontWeight="medium" mb={1}>
								Detalle de productos solicitados en el pedido
							</SoftTypography>
							{resumenPF.length === 0 ? null : (
								<SoftBox display="flex" flexDirection="column" gap={1}>
									{resumenPF.map((pf) => {
										const expanded = !!pfGlobalExpanded[pf.idProducto];
										const desglose = desglosesPorPF.get(pf.idProducto) || [];

										return (
											<SoftBox key={`pf-global-${pf.idProducto}`} px={2} py={1} sx={{ border: "1px solid rgba(0,0,0,0.06)", borderRadius: 1 }}>
												<SoftBox display="flex" alignItems="center" justifyContent="space-between">
													<SoftBox display="flex" gap={2} alignItems="center">
														<Icon color="warning">shopping_bag</Icon>
														<SoftTypography variant="button" fontWeight="medium">{pf.cantidadPF}</SoftTypography>
														{Number(pf?.porciones) > 0 && (
															<SoftTypography variant="caption" color="secondary" sx={{ ml: -2 }}>
																&nbsp;x {pf.porciones} {Number(pf.porciones) === 1 ? "porción" : "porciones"}
															</SoftTypography>
														)}
														<SoftTypography variant="button" fontWeight="medium">{pf.nombre}</SoftTypography>
														<SoftTypography variant="caption" color="secondary">• Total de filas afectadas: {pf.usos}</SoftTypography>
													</SoftBox>
													<SoftBox display="flex" gap={1} alignItems="center">
														<Tooltip title={expanded ? "Ocultar desglose" : "Ver desglose por MP"}>
															<SoftButton
																variant="outlined"
																color="secondary"
																size="small"
																iconOnly
																circular
																onClick={() => setPfGlobalExpanded((prev) => ({ ...prev, [pf.idProducto]: !expanded }))}
															>
																<Icon>{expanded ? "expand_less" : "expand_more"}</Icon>
															</SoftButton>
														</Tooltip>
														{allowEdit && <Tooltip title="Quitar este producto final del pedido">
															<SoftButton
																color="error"
																size="small"
																disabled={!allowEdit}
																onClick={() => {
																	confirmar({
																		title: "Quitar producto final",
																		message: `Esto quitará todas las contribuciones del PF "${pf.nombre}" en el pedido. ¿Desea continuar?`,
																		func: () => {
																			setData((prev) => {
																				const detalles = (prev.detalles || []).map((det) => {
																					const previousSumFuentes = sumFuentesMP(det);
																					const fuentes = (det.fuentesPF || []).filter((f) => f.idProducto !== pf.idProducto);
																					const nuevo = restoreOriginalAjusteAfterFuenteChange({ ...det, fuentesPF: fuentes }, previousSumFuentes);
																					const recalculado = recalcFromFuentes(nuevo);
																					return recalculado;
																				}).filter((d) => (Number(d?.cantidad) || 0) > 0);
																				return { ...prev, detalles };
																			});
																		},
																	});
																}}
															>
																<Icon>delete</Icon>&nbsp;Quitar producto final
															</SoftButton>
														</Tooltip>}
													</SoftBox>
												</SoftBox>
												<Collapse in={expanded} timeout="auto" unmountOnExit>
													<SoftBox mt={1} display="flex" flexDirection="column" gap={1}>
														{desglose.length === 0 ? (
															<SoftTypography variant="caption" color="secondary" px={1}>Sin desglose disponible.</SoftTypography>
														) : (
															desglose.map((mp) => (
																<SoftBox key={`pf-${pf.idProducto}-mp-${mp.idMateriaPrima}`} display="flex" alignItems="center" justifyContent="space-between" px={1} py={0.5} sx={{ border: "1px dashed rgba(0,0,0,0.08)", borderRadius: 1 }}>
																	<SoftBox display="flex" gap={2} alignItems="center">
																		<Icon color="info">inventory_2</Icon>
																		<SoftTypography variant="button">{mp.nombre}</SoftTypography>
																		<SoftTypography variant="caption" color="secondary">• Aporte MP: {formatMoneyPunto(mp.aporteMP)}{" "}{mp.unidad}</SoftTypography>
																	</SoftBox>
																	{allowEdit && <SoftBox>
																		<Tooltip title="Quitar de la lista">
																			<SoftButton
																				variant="outlined"
																				color="error"
																				size="small"
																				disabled={!allowEdit}
																				onClick={allowEdit ? () => {
																					confirmar({
																						title: "Quitar de la lista",
																						message: `Esto quitará las contribuciones del PF "${pf.nombre}" solo de la MP "${mp.nombre}". ¿Desea continuar?`,
																						func: () => {
																							setData((prev) => {
																								let detalles = (prev.detalles || []).map((det) => {
																									const mpId = det?.materiaPrima?.id || det?.idMateriaPrima;
																									if (mpId !== mp.idMateriaPrima) return det;
																									const previousSumFuentes = sumFuentesMP(det);
																									const fuentes = (det.fuentesPF || []).filter((f) => f.idProducto !== pf.idProducto);
																									const nuevo = restoreOriginalAjusteAfterFuenteChange({ ...det, fuentesPF: fuentes }, previousSumFuentes);
																									const recalculado = recalcFromFuentes(nuevo);
																									return recalculado;
																								});
																								detalles = detalles.filter((d) => (Number(d?.cantidad) || 0) > 0);
																								return { ...prev, detalles };
																							});
																						},
																					});
																				} : () => { }}
																			>
																				<Icon>delete_sweep</Icon>&nbsp;Quitar
																			</SoftButton>
																		</Tooltip>
																	</SoftBox>}
																</SoftBox>
															))
														)}
													</SoftBox>
												</Collapse>
											</SoftBox>
										);
									})}
								</SoftBox>
							)}
						</SoftBox>
					)}

					{data?.detalles?.length > 0 ? (
						<>
							<DataTableCustom
								styleCard={data?.tipoDetalle === "Productos" ? {
									"& > div:first-of-type": { overscrollBehaviorX: "contain" },
									"& > div:first-of-type > div": { minWidth: "1220px" },
								} : {}}
								headerStyles={{
									backgroundColor: "#ffffff",             // fondo blanco
									borderBottom: "1.5px solid #070606ff",
									py: 1.1,                               // menos alto
									"& .MuiTypography-root": {
										fontWeight: 600,                      // negrita media
										color: "#344767",                     // gris azulado
										// textTransform: "none",                // sin uppercase
										fontSize: "0.7rem",
										letterSpacing: 0.2,
									},
								}}
								columns={columns}
								data={(data?.detalles || []).filter(d => !d._toRemove)}
								loading={false}
								loadingActions={false}
								sortable={false}
								autoCalculateWidths={false}
								maxHeight={fullscreen ? "75vh" : "45vh"}
								emptyMessage="No hay detalles agregados"
								showActions={false}
								expandedRowRender={(row) => {
									const showEditor = !!row?._editObservacion;
									const showFuentes = Array.isArray(row?.fuentesPF) && row.fuentesPF.length > 0;
									if (!showEditor && !showFuentes) return null;
									return (
										<SoftBox px={2} py={1} display="flex" flexDirection="column" gap={1} sx={{ backgroundColor: "rgba(0,0,0,0.02)", borderRadius: 1, mt: 1 }}>
											{showEditor && (
												<SoftBox display="flex" alignItems="center" gap={1} sx={{ p: 1 }}>
													<SoftInput
														autoFocus
														placeholder="Escriba una observación"
														value={row?._observacionDraft || ""}
														onChange={(e) => {
															const val = (e.target.value || "").slice(0, 65535);
															setData((prev) => {
																const detalles = prev?.detalles || [];
																const index = detalles.findIndex((d) => d.id === row.id);
																if (index === -1) return prev;
																const item = { ...detalles[index], _observacionDraft: val };
																const nuevos = detalles.map((d, i) => (i === index ? item : d));
																return { ...prev, detalles: nuevos };
															});
														}}
														onKeyDown={(e) => {
															if (e.key === 'Enter') {
																e.preventDefault();
																saveEditObservacion(row.id);
															}
															if (e.key === 'Escape') {
																e.preventDefault();
																cancelEditObservacion(row.id);
															}
														}}
														inputProps={{ maxLength: 65535 }}
													/>
													<SoftBox display="flex" gap={1} ml={1}>
														<Tooltip title="Guardar (Enter)">
															<SoftButton
																variant="outlined"
																color="success"
																size="small"
																iconOnly
																circular
																onClick={() => saveEditObservacion(row.id)}
															>
																<Icon fontSize="small">save</Icon>
															</SoftButton>
														</Tooltip>
														<Tooltip title="Cancelar (Esc)">
															<SoftButton
																variant="outlined"
																color="error"
																size="small"
																iconOnly
																circular
																onClick={() => cancelEditObservacion(row.id)}
															>
																<Icon fontSize="small">close</Icon>
															</SoftButton>
														</Tooltip>
													</SoftBox>
												</SoftBox>
											)}
											{showFuentes && (
												<>
													<SoftTypography variant="button" color="secondary">
														Productos finales que aportan a esta materia prima
													</SoftTypography>
													{(row.fuentesPF || []).map((f, idx) => (
														<SoftBox key={`${row.id}_f_${f.idProducto}_${idx}`} display="flex" alignItems="center" gap={5} sx={{ pb: 0.5, px: 5 }}>
															<SoftBox display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
																<SoftTypography variant="caption" fontWeight="medium">{f?.nombreProducto || f?.idProducto}</SoftTypography>
															</SoftBox>
															<SoftBox display="flex" alignItems="center" gap={1}>
																<SoftTypography variant="caption" color="secondary">Prod. final agregado: {f?.cantidadPF ?? 0}</SoftTypography>
															</SoftBox>
															<SoftBox display="flex" alignItems="center" gap={1}>
																<SoftTypography variant="caption" color="secondary">aporte</SoftTypography>
																<SoftInputNumber
																	value={Number(f?.cantidadMP) || 0}
																	disabled={!allowEdit}
																	onChange={(e) => {
																		if (!allowEdit) return;
																		const val = Number(e.target.value);
																		const nuevoVal = Number.isNaN(val) || val < 0 ? 0 : val;
																		setData((prev) => {
																			const detalles = prev?.detalles || [];
																			const index = detalles.findIndex(d => d.id === row.id);
																			if (index === -1) return prev;
																			const det = { ...detalles[index] };
																			const previousSumFuentes = sumFuentesMP(det);
																			const fuentes = [...(det.fuentesPF || [])];
																			const fIndex = fuentes.findIndex(x => x.idProducto === f.idProducto && x.cantidadPF === f.cantidadPF && x.cantidadMP === f.cantidadMP);
																			const targetIdx = fIndex >= 0 ? fIndex : idx;
																			const fuente = { ...(fuentes[targetIdx] || f) };
																			fuente.cantidadMP = nuevoVal;
																			fuentes[targetIdx] = fuente;
																			const normalizado = restoreOriginalAjusteAfterFuenteChange({ ...det, fuentesPF: fuentes }, previousSumFuentes);
																			const recalculado = recalcFromFuentes(normalizado);
																			const nuevos = detalles.map((d, i) => i === index ? recalculado : d);
																			return { ...prev, detalles: nuevos };
																		});
																	}}
																/>
																<SoftTypography variant="caption" color="secondary">{row?.materiaPrima?.tipoUnidad?.abreviatura}</SoftTypography>
															</SoftBox>

															<SoftBox display="flex" alignItems="center" gap={1}>
																<Tooltip title="Quitar esta fuente" placement="top">
																	<SoftButton
																		variant="outlined"
																		color="error"
																		size="small"
																		iconOnly
																		circular
																		disabled={!allowEdit}
																		onClick={allowEdit ? () => {
																			confirmar({
																				title: "Quitar fuente",
																				message: `¿Desea quitar la contribución de ${f?.nombreProducto || f?.idProducto}?`,
																				func: () => {
																					setData((prev) => {
																						const detalles = prev?.detalles || [];
																						const index = detalles.findIndex(d => d.id === row.id);
																						if (index === -1) return prev;
																						const det = { ...detalles[index] };
																						const previousSumFuentes = sumFuentesMP(det);
																						const fuentes = (det.fuentesPF || []).filter((x, j) => j !== idx);
																						const normalizado = restoreOriginalAjusteAfterFuenteChange({ ...det, fuentesPF: fuentes }, previousSumFuentes);
																						const recalculado = recalcFromFuentes(normalizado);
																						// Si total queda en 0, eliminar el item
																						if ((Number(recalculado?.cantidad) || 0) <= 0) {
																							const restantes = detalles.filter((_, i) => i !== index);
																							return { ...prev, detalles: restantes };
																						}
																						const nuevos = detalles.map((d, i) => i === index ? recalculado : d);
																						return { ...prev, detalles: nuevos };
																					});
																				}
																			});
																		} : () => { }}
																	>
																		<Icon fontSize="small">delete</Icon>
																	</SoftButton>
																</Tooltip>
															</SoftBox>
														</SoftBox>
													))}
												</>
											)}
										</SoftBox>
									);
								}}
								rowExpandable={(row) => (row?._editObservacion === true || (row?.fuentesPF?.length > 0))}
								getRowId={(row) => row.id}
								expandedKeys={expandedKeys}
								onExpandChange={(keys) => setExpandedKeys(keys)}
							/>
							{/* Dialog de Observación (solo lectura) */}
							<Dialog open={obsViewOpen} onClose={closeObservacionDialog} maxWidth="sm" fullWidth>
								<DialogTitle>Observación</DialogTitle>
								<DialogContent>
									<TextField
										fullWidth
										multiline
										minRows={3}
										value={obsViewText}
										InputProps={{ readOnly: true }}
									/>
								</DialogContent>
								<DialogActions>
									<Button onClick={closeObservacionDialog}>Cerrar</Button>
								</DialogActions>
							</Dialog>
						</>
					) : (
						<SinDatos />
					)}
				</SoftBox>
				{/* Información necesaria para confeccionar el pliego particular */}
				{data?.tipoCompra === "Concurso" && (
					<SoftBox sx={{ width: "100%" }} p={3} mt={1.5}>
						<SoftTypography variant="h6" mb={1}>
							Información necesaria para confeccionar el pliego particular
						</SoftTypography>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={4}>
								<Grid container spacing={2}>
									<Grid item xs={12}>
										<InputLabel htmlFor="pliegoVencimientoDias">
											<SoftTypography component="label" variant="caption" fontWeight="bold">
												Tiempo de vencimiento del producto (en días)
											</SoftTypography>
										</InputLabel>
										<Tooltip title={getError("pliegoVencimientoDias") || ""} placement="right">
											<TextField
												fullWidth
												type="number"
												size="small"
												name="pliegoVencimientoDias"
												value={data?.pliegoVencimientoDias ?? ""}
												onChange={(e) => {
													const raw = e.target.value;
													const num = raw === "" ? null : Number.isNaN(Number(raw)) ? null : parseInt(raw, 10);
													handlePliegoField(e.target.name, num);
													setData((prev) => ({ ...prev, [e.target.name]: num }));
												}}
												inputProps={{ min: 1 }}
												placeholder="Ingrese días"
												error={Boolean(getError("pliegoVencimientoDias"))}
												disabled={!allowEdit}
												sx={{
													'& input[type=number]': { MozAppearance: 'textfield' },
													'& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
														WebkitAppearance: 'none',
														margin: 0,
													},
												}}
											/>
										</Tooltip>
									</Grid>
									<Grid item xs={12}>
										<InputLabel htmlFor="pliegoRequiereDemanda">
											<SoftTypography component="label" variant="caption" fontWeight="bold">
												Requiere entregas a demanda (*)
											</SoftTypography>
										</InputLabel>
										<Tooltip title={getError("pliegoRequiereDemanda") || ""} placement="right">
											<Select
												name="pliegoRequiereDemanda"
												value={data?.pliegoRequiereDemanda ?? ""}
												onChange={(e) => {
													handlePliegoField(e.target.name, e.target.value);
													setData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
												}}
												fullWidth
												displayEmpty
												error={Boolean(getError("pliegoRequiereDemanda"))}
												disabled={!allowEdit}
												sx={{ mt: 0.6 }}
											>
												<MenuItem value="" disabled>
													Seleccione una opción
												</MenuItem>
												<MenuItem value="SI">SI</MenuItem>
												<MenuItem value="NO">NO</MenuItem>
											</Select>
										</Tooltip>
									</Grid>
									<Grid item xs={12}>
										<InputLabel htmlFor="pliegoParticularidades">
											<SoftTypography component="label" variant="caption" fontWeight="bold">
												Particularidades a tener en cuenta
											</SoftTypography>
										</InputLabel>
										<TextField
											fullWidth
											multiline
											minRows={3}
											maxRows={6}
											size="small"
											name="pliegoParticularidades"
											value={data?.pliegoParticularidades || ""}
											onChange={(e) => setData((prev) => ({ ...prev, [e.target.name]: e.target.value }))}
											placeholder="Ingrese texto"
											disabled={!allowEdit}
										/>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</SoftBox>
				)}
			</Card>
			{/* Dialogo de Equivalencia Pack <-> Producto */}
			<Dialog open={equivOpen} onClose={closeEquivalenciaDialog} maxWidth="xs" fullWidth>
				<DialogTitle component="div">
					{(() => {
						const item = equivIndex != null ? data?.detalles?.[equivIndex] : null;
						const unidadPack = (equivUnidadPack || item?.unidadPack || 'pack');
						const unidadMP = item?.materiaPrima?.tipoUnidad?.abreviatura || item?.materiaPrima?.tipoUnidad?.nombre || item?.materiaPrima?.tipoUnidad || 'producto';
						return (
							<SoftTypography variant="h6">{`Equivalencia entre ${unidadPack} y ${unidadMP}`}</SoftTypography>
						);
					})()}
				</DialogTitle>
				<DialogContent>
					{/* Unidad del pack */}
					<SoftBox pb={2}>
						<InputLabel htmlFor="unidadPack">
							<SoftTypography component="label" variant="caption" fontWeight="bold">
								Unidad del pack*
							</SoftTypography>
						</InputLabel>
						<SoftInput
							id="unidadPack"
							placeholder="Ej: Caja, Paquete, Lata, Maple, etc."
							value={equivUnidadPack}
							onChange={(e) => {
								const v = (e.target.value || "").slice(0, 30);
								setEquivUnidadPack(v);
							}}
						/>
						<SoftTypography variant="caption" color="secondary">
							{equivUnidadPack?.trim()?.length > 30
								? "Máximo 30 caracteres"
								: ""}
						</SoftTypography>
					</SoftBox>
					<SoftBox display="flex" alignItems="center" justifyContent="space-between" gap={3} py={1}>
						<SoftBox display="flex" flexDirection="column" alignItems="stretch" sx={{ minWidth: 0, flex: 1 }}>
							{(() => {
								const item = equivIndex != null ? data?.detalles?.[equivIndex] : null;
								const unidadPack = (equivUnidadPack || item?.unidadPack || 'pack');
								return (
									<SoftTypography variant="button" color="secondary" mb={0.5}>{unidadPack}</SoftTypography>
								);
							})()}
							<SoftInputNumber
								name="packs"
								value={equivPacks}
								onChange={(e) => {
									const v = Math.max(1, Math.round(Number(e.target.value) || 1));
									setEquivPacks(v);
								}}
							/>
						</SoftBox>

						<SoftBox display="flex" alignItems="center" flexDirection="column" justifyContent="space-between" gap={.5} pt={2}>
							<Icon sx={{ color: getUproColor("uproGreen") }}>sync_alt</Icon>
							<SoftTypography variant="caption" color="secondary" mb={0.5}>Equivale a</SoftTypography>
						</SoftBox>

						<SoftBox display="flex" flexDirection="column" alignItems="stretch" sx={{ minWidth: 0, flex: 1 }}>
							{(() => {
								const item = equivIndex != null ? data?.detalles?.[equivIndex] : null;
								const unidadMP = item?.materiaPrima?.tipoUnidad?.abreviatura || item?.materiaPrima?.tipoUnidad?.nombre || item?.materiaPrima?.tipoUnidad || 'producto';
								return (
									<SoftTypography variant="button" color="secondary" mb={0.5}>{unidadMP}</SoftTypography>
								);
							})()}
							<SoftInputNumber
								name="materiaPrima"
								value={equivMP}
								onChange={(e) => {
									const raw = Number(e.target.value);
									const v = isNaN(raw) ? 1 : Math.max(0.000001, raw);
									setEquivMP(v);
								}}
							/>
						</SoftBox>
					</SoftBox>
				</DialogContent>
				<DialogActions>
					<SoftButton color="secondary" variant="outlined" onClick={closeEquivalenciaDialog}>Cancelar</SoftButton>
					<SoftButton
						color="primary"
						onClick={saveEquivalencia}
						disabled={(() => {
							const unidad = (equivUnidadPack || "").trim();
							const unidadOk = unidad.length > 0 && unidad.length <= 30;
							const packsOk = Math.max(1, Math.round(Number(equivPacks) || 0)) >= 1;
							const mpOk = Number(equivMP) > 0;
							return !(unidadOk && packsOk && mpOk);
						})()}
					>
						Guardar
					</SoftButton>
				</DialogActions>
			</Dialog>
			{/* Modal: Productos finales */}
			<Dialog
				open={openModalProductosFinales}
				onClose={() => {
					setOpenModalProductosFinales(false);
					setPfSelected(null);
					setPfCantidad(1);
					setPfOpenResults(false);
				}}
				maxWidth="md"
				fullWidth
			>
				<DialogTitle>
					<SoftTypography variant="h6">Productos finales</SoftTypography>
				</DialogTitle>
				<DialogContent>
					{/* Buscador */}
					<SoftBox display="flex" alignItems="center" gap={1} py={2}>
						<SoftInput
							placeholder="Buscar producto..."
							value={pfSearch}
							onChange={(e) => setPfSearch(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									getProductosFinales({
										variables: {
											search: pfSearch || null,
											order: { field: "nombre", order: "ASC" },
											sinIngredientes: false,
											materiaPrimaDeshabilitada: false,
										},
									});
									setPfOpenResults(true);
								}
							}}
						/>
						<SoftButton
							variant="gradient"
							color="info"
							iconOnly
							circular
							onClick={() => {
								getProductosFinales({
									variables: {
										search: pfSearch || null,
										order: { field: "nombre", order: "ASC" },
										sinIngredientes: false,
										materiaPrimaDeshabilitada: false,
									},
								});
								setPfOpenResults(true);
							}}
							aria-label="Buscar"
						>
							<Icon>search</Icon>
						</SoftButton>
					</SoftBox>

					{/* Resultados de búsqueda */}
					{pfOpenResults && (
						<Card sx={{ mb: 2, mt: -1, ml: -1 }}>
							<SoftBox sx={{ maxHeight: 220, overflowY: "auto" }}>
								{pfLoading ? (
									<Loading />
								) : pfData?.productos?.length ? (
									pfData.productos.map((p) => {
										const yaAgregado = pfIdsAgregados.has(String(p.id));
										return (
										<MenuItem
											key={p.id}
											disabled={yaAgregado}
											onClick={() => {
												if (yaAgregado) return;
												setPfSelected(p);
												setPfOpenResults(false);
												setPfSearch("");
											}}
											sx={{ whiteSpace: "normal" }}
										>
											<SoftBox display="flex" alignItems="center" gap={1}>
												<Icon sx={{ color: getUproColor("uproPink") }}>layers</Icon>
												<SoftTypography variant="caption" fontWeight="bold">
													{`${p.id} - ${p.nombre}${yaAgregado ? " (Ya agregado)" : ""}`}
												</SoftTypography>
												<SoftTypography variant="caption" fontWeight="regular">rinde:{p.cantidadPorciones == 1 ? " 1 porción" : `${p.cantidadPorciones} porciones`}</SoftTypography>
											</SoftBox>
										</MenuItem>
										);
									})
								) : (
									<SinResultados />
								)}
							</SoftBox>
						</Card>
					)}

					{/* Seleccionado + Cantidad */}
					{pfSelected ? (
						<SoftBox display="flex" alignItems="center" justifyContent="space-between" gap={2} mt={2}>
							<SoftTypography variant="subtitle2" sx={{ flex: 1 }}>
								{pfSelected?.nombre || pfSelected?.id}
							</SoftTypography>
							<SoftTypography variant="subtitle2">Cant:</SoftTypography>
							<TextField
								value={pfCantidad}
								onChange={(e) => {
									const raw = e.target.value;
									const num = raw === "" ? "" : parseInt(raw, 10);
									if (raw === "") return setPfCantidad("");
									if (!Number.isNaN(num)) setPfCantidad(Math.max(1, num));
								}}
								type="number"
								size="small"
								inputProps={{ min: 1, step: 1 }}
								sx={{
									width: 120,
									'& input[type=number]': { MozAppearance: 'textfield' },
									'& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
										WebkitAppearance: 'none',
										margin: 0,
									},
								}}
							/>
							<Icon onClick={() => setPfSelected(null)} sx={{ color: getUproColor("uproPink") }}>cancel</Icon>
						</SoftBox>
					) : (
						<SoftTypography variant="caption" color="secondary">
							Seleccione un producto de la lista para continuar
						</SoftTypography>
					)}
				</DialogContent>
				<DialogActions>
					<SoftButton
						color="primary"
						onClick={() => {
							if (!pfSelected || !pfCantidad || Number(pfCantidad) < 1) return;

							getPFMateriasPrimas({ variables: { idProducto: pfSelected.id } })
								.then(({ data: resp }) => {
									const base = resp?.productosFinalesMateriasPrimas || [];
									if (!Array.isArray(base) || base.length === 0) {
										// Cerrar igual
										setOpenModalProductosFinales(false);
										setPfSelected(null);
										setPfCantidad(1);
										setPfOpenResults(false);
										return;
									}
									setData((prev) => {
										const detallesPrev = prev?.detalles || [];
										const nuevos = [...detallesPrev];

										const cantPF = Math.max(1, parseInt(pfCantidad, 10) || 1);

										// Armar un mapa de existentes (solo requierePack=false), preservando nombreCompra
										const existentesMap = new Map();
										nuevos.forEach((d, i) => {
											if (!d?._toRemove && !d?.requierePack && d?.materiaPrima?.id) {
												existentesMap.set(d.materiaPrima.id, { index: i, item: d });
											}
										});

										base.forEach((mp) => {
											const mpId = mp.id;
											const unidadAbrev = mp?.tipoUnidad?.abreviatura || null;
											// Precio sugerido del catálogo; sin precio cargado queda en 0 para que el usuario lo complete
											const precioUnit = mp?.precio ?? 0;
											const cantidadBase = Number(mp?.cantidadIngredienteUnidadMP) || 0;
											const cantidadMPExacta = cantidadBase * cantPF;

											const existente = existentesMap.get(mpId);
											if (existente) {
												const idx = existente.index;
												const item = { ...nuevos[idx] };
												const previousSumFuentes = sumFuentesMP(item);
												// El snapshot se conserva en el renglón, no en la materia prima.
												item.materiaPrima = { ...(item.materiaPrima || {}), ...mp };

												// fuentesPF: agregar/actualizar contribución de este PF
												const fuentes = Array.isArray(item.fuentesPF) ? [...item.fuentesPF] : [];
												const fIndex = fuentes.findIndex((f) => f?.idProducto === pfSelected.id);
												if (fIndex >= 0) {
													const f = { ...fuentes[fIndex] };
													f.cantidadPF = (Number(f.cantidadPF) || 0) + cantPF;
													f.cantidadMP = (Number(f.cantidadMP) || 0) + cantidadMPExacta;
													// Guardar la cantidad de porciones asociada a esta fuente (PF)
													f.cantidadPorciones = pfSelected.cantidadPorciones ?? f.cantidadPorciones;
													fuentes[fIndex] = f;
												} else {
													fuentes.push({
														idProducto: pfSelected.id,
														nombreProducto: pfSelected.nombre,
														cantidadPF: cantPF,
														cantidadMP: cantidadMPExacta,
														cantidadPorciones: pfSelected.cantidadPorciones,
													});
												}
												item.fuentesPF = fuentes;
												const normalizado = restoreOriginalAjusteAfterFuenteChange(item, previousSumFuentes);

												// Recalcular basado en fuentes + ajusteManual
												const recalculado = recalcFromFuentes(normalizado);
												recalculado.unidad = unidadAbrev;
												recalculado.requierePack = false;
												recalculado.unidadPack = null;
												recalculado.equivalenciaPack = undefined;
												// El precio estimado del ítem existente se conserva (recalcFromFuentes no lo pisa)
												nuevos[idx] = recalculado;
											} else {
												const nuevoItemBase = {
													id: idTemporal(),
													materiaPrima: { ...mp },
													nombreCompra: mp?.nombre || "",
													cantidad: 0,
													unidad: unidadAbrev,
													// precioEstimado unitario
													precioEstimado: precioUnit,
													observacion: null,
													requierePack: false,
													unidadPack: null,
													cantidadPack: 1,
													totalMP: 0,
													// Mantener porciones en el detalle nuevo por compatibilidad
													porciones: pfSelected.cantidadPorciones,
													fuentesPF: [{
														idProducto: pfSelected.id,
														nombreProducto: pfSelected.nombre,
														cantidadPF: cantPF,
														cantidadMP: cantidadMPExacta,
														cantidadPorciones: pfSelected.cantidadPorciones,
													}],
													ajusteManual: 0,
												};
												const nuevoItem = recalcFromFuentes(nuevoItemBase);
												nuevos.push(nuevoItem);
											}
										});

										return { ...prev, detalles: nuevos };
									});

									setOpenModalProductosFinales(false);
									setPfSelected(null);
									setPfCantidad(1);
									setPfOpenResults(false);
								}).catch(() => {
									// Ante error, simplemente no cerrar ni modificar
								});
						}}
						disabled={!pfSelected || !pfCantidad || Number(pfCantidad) < 1 || pfmpLoading}
					>
						Aceptar
					</SoftButton>
					<SoftButton
						color="secondary"
						variant="outlined"
						onClick={() => {
							setOpenModalProductosFinales(false);
							setPfSelected(null);
							setPfCantidad(1);
							setPfOpenResults(false);
						}}
					>
						Cerrar
					</SoftButton>
				</DialogActions>
			</Dialog>
			<Card>
				<SoftBox display="flex" justifyContent="space-between" gap={1}>
					{false && data.tipoDetalle === "Productos" ? (
						<DinamicCard
							noBg
							title="Verificación almacén"
							data={
								data.stockEstado == "Verificado"
									? {
										Responsable: `${data.stockUser?.nombre || ""} ${data.stockUser?.apellido}`,
										Fecha: data.stockFecha,
									}
									: {}
							}
							actions={
								data.stockEstado == "Sin registro" && data.detalles?.length > 0
									? [
										{
											name: "Solicitar",
											tooltip: "Solicitar verificación de stock a almacén",
											color: "uproGreen",
											icon: "arrow_upward",
											action: () => {
												confirmar({
													title: "Solicitar verificación de stock",
													message:
														"El almacén verificará la cantidad óptima a pedir. Una vez solicitada esta verificación no podrán cambiarse los productos de este pedido.",
													pedirMotivo: true,
													placeholder: "Observaciones para almacén",
													func: (motivo) => {
														setData((prev) => ({
															...prev,
															stockEstado: "Pendiente",
															stockObservaciones: motivo,
														}));
													},
												});
											},
										},
									]
									: (data.stockEstado === "Verificado" || data.stockEstado === "Pendiente") &&
										data.licitacionEstado == "No iniciada" &&
										data.estados?.[0]?.finalizado !== true
										? [
											{
												name: "Reiniciar",
												tooltip:
													"Reiniciar la verificación, esto reiniciará la fecha de inicio del pedido",
												color: "uproGreen",
												icon: "refresh",
												action: () => {
													confirmar({
														title: "Reiniciar verificación de stock",
														message:
															"¿Estás seguro de que deseas reiniciar la verificación de stock? Esto restablecerá el proceso de verificación.",
														func: () => {
															setData((prev) => ({
																...prev,
																stockEstado: "Sin registro",
																detalles: data.detalles.map((item) => ({
																	...item,
																	cantidadVerificada: null,
																})),
															}));
														},
													});
												},
											},
										]
										: []
							}
						>
							<SoftBox display="flex" justifyContent="end">
								<SoftTypography variant="caption" color="secondary">
									{data.stockEstado}
								</SoftTypography>
							</SoftBox>
						</DinamicCard>
					) : (
						<div></div>
					)}
					<SoftBox p={2} textAlign="right">
						<SoftTypography variant="h6" color="dark" fontWeight="medium">
							Total: $ {formatMoneyPunto(total)}
						</SoftTypography>
					</SoftBox>
				</SoftBox>
				{/* Input compartido para adjuntar un archivo a un ítem (ver toggleInput) */}
				<input key={keyFile} ref={fileRef} type="file" hidden onChange={handleFile} />
			</Card>
		</SoftBox>
	);
}
